import { createSlice } from '@reduxjs/toolkit';
import { 
  fetchClinics, 
  fetchClinicById, 
  createClinic, 
  updateClinic, 
  deleteClinic,
  fetchClinicStaff,
  addStaffToClinic,
  removeStaffFromClinic,
  fetchAvailableStaff
} from '../thunks/clinicThunk';

export const clinicSlice = createSlice({
  name: 'clinics',
  initialState: {
    clinics: [],
    status: "idle",
    error: null,
    selectedClinic: null,
    selectedClinicsForSchedule: [], // For multi-clinic schedule filtering
    staff: [], // Staff members for the selected clinic
    staffStatus: "idle",
    staffError: null,
    availableStaff: [], // Available users to add as staff
    availableStaffStatus: "idle",
    availableStaffError: null,
    pagination: {
        totalClients: 0,
        currentPage: 1,
        totalPages: 1,
        pageSize: 20,
      },
  },
  reducers: {
    setSelectedClinic: (state, action) => {
      state.selectedClinic = action.payload;
    },
    clearSelectedClinic: (state) => {
      state.selectedClinic = null;
    },
    setSelectedClinicsForSchedule: (state, action) => {
      state.selectedClinicsForSchedule = action.payload;
    },
    clearSelectedClinicsForSchedule: (state) => {
      state.selectedClinicsForSchedule = [];
    },
    toggleClinicForSchedule: (state, action) => {
      const clinicId = action.payload;
      if (!state.selectedClinicsForSchedule) {
        state.selectedClinicsForSchedule = [];
      }
      if (!state.clinics) {
        state.clinics = [];
      }
      const existingIndex = state.selectedClinicsForSchedule.findIndex(c => c._id === clinicId);
      if (existingIndex >= 0) {
        state.selectedClinicsForSchedule.splice(existingIndex, 1);
      } else {
        const clinic = state.clinics.find(c => c._id === clinicId);
        if (clinic) {
          state.selectedClinicsForSchedule.push(clinic);
        }
      }
    },
  }, // You might add other reducers here for local state changes
  extraReducers(builder) {
    builder
      // Fetch all clinics
      .addCase(fetchClinics.pending, (state) => {
        state.status = 'loading';
        state.error = null; // Clear any previous error     
      })
      .addCase(fetchClinics.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.clinics = Array.isArray(action.payload) ? action.payload : [];
        if (state.clinics.length > 0) {  
          state.selectedClinic = state.clinics[0];
          // Initialize schedule selection as empty (shows "All Clinics")
          // Don't auto-select all clinics - let user choose
          if (!state.selectedClinicsForSchedule || state.selectedClinicsForSchedule.length === 0) {
            state.selectedClinicsForSchedule = [];
          }
        }
        state.error = null; // Clear any previous error
      })
      .addCase(fetchClinics.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload || action.error.message;
      })

      // Fetch single clinic
      .addCase(fetchClinicById.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClinicById.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.selectedClinic = action.payload;
        state.error = null;
      })
      .addCase(fetchClinicById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Create clinic
      .addCase(createClinic.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(createClinic.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.clinics.push(action.payload);
        state.error = null;
      })
      .addCase(createClinic.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Update clinic
      .addCase(updateClinic.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(updateClinic.fulfilled, (state, action) => { 
        state.status = 'succeeded';
        const index = state.clinics.findIndex(clinic => clinic._id === action.payload._id);
        if (index !== -1) {
          state.clinics[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updateClinic.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Delete clinic
      .addCase(deleteClinic.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(deleteClinic.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.clinics = state.clinics.filter(clinic => clinic._id !== action.payload);
        state.error = null;
      })
      .addCase(deleteClinic.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // Fetch clinic staff
      .addCase(fetchClinicStaff.pending, (state) => {
        state.staffStatus = 'loading';
        state.staffError = null;
      })
      .addCase(fetchClinicStaff.fulfilled, (state, action) => {
        state.staffStatus = 'succeeded';
        state.staff = action.payload;
        state.staffError = null;
      })
      .addCase(fetchClinicStaff.rejected, (state, action) => {
        state.staffStatus = 'failed';
        state.staffError = action.payload || action.error.message;
      })

      // Add staff to clinic
      .addCase(addStaffToClinic.pending, (state) => {
        state.staffStatus = 'loading';
        state.staffError = null;
      })
      .addCase(addStaffToClinic.fulfilled, (state, action) => {
        state.staffStatus = 'succeeded';
        // Add the new staff member to the staff array
        if (action.payload.user) {
          state.staff.push(action.payload.user);
        }
        state.staffError = null;
      })
      .addCase(addStaffToClinic.rejected, (state, action) => {
        state.staffStatus = 'failed';
        state.staffError = action.payload || action.error.message;
      })

      // Remove staff from clinic
      .addCase(removeStaffFromClinic.pending, (state) => {
        state.staffStatus = 'loading';
        state.staffError = null;
      })
      .addCase(removeStaffFromClinic.fulfilled, (state, action) => {
        state.staffStatus = 'succeeded';
        // Remove the staff member from the staff array
        state.staff = state.staff.filter(staff => staff._id !== action.meta.arg.staffId);
        state.staffError = null;
      })
      .addCase(removeStaffFromClinic.rejected, (state, action) => {
        state.staffStatus = 'failed';
        state.staffError = action.payload || action.error.message;
      })

      // Fetch available staff
      .addCase(fetchAvailableStaff.pending, (state) => {
        state.availableStaffStatus = 'loading';
        state.availableStaffError = null;
      })
      .addCase(fetchAvailableStaff.fulfilled, (state, action) => {
        state.availableStaffStatus = 'succeeded';
        state.availableStaff = action.payload;
        state.availableStaffError = null;
      })
      .addCase(fetchAvailableStaff.rejected, (state, action) => {
        state.availableStaffStatus = 'failed';
        state.availableStaffError = action.payload || action.error.message;
      });
  },
});

export const { 
  setSelectedClinic, 
  clearSelectedClinic, 
  setSelectedClinicsForSchedule, 
  clearSelectedClinicsForSchedule, 
  toggleClinicForSchedule 
} = clinicSlice.actions;
export default clinicSlice.reducer;
